// eslint-disable-next-line @typescript-eslint/no-use-before-define
import React, { useEffect, useState } from 'react';
import { Button, Grid, IconButton, Paper, withStyles } from '@material-ui/core';
import DeleteIcon from '@material-ui/icons/Delete';
import {
    FormHeading,
    FullWidthRichTextField,
    FullWidthTextField,
    FullWidthSelectField,
} from '../../forms';
import styles, { StyledComponent } from '../../styles/styles';
import {
    getRetrospectiveUnderwritingDecision,
    NonDisclosureDetails,
    RetrospectiveUnderwritingDecisionDetails,
} from '../reducer';
import { connect } from 'react-redux';
import { conditionalRequired, requiredValidator } from '../../forms/validations';
import { useForm } from 'react-final-form';

const REASON_OPTIONS = [
    {
        value: 'abnormal-investigations',
        label: 'Abnormal investigations',
        categories: [
            'Blood tests',
            'ECG / echo',
            'GI scope',
            'Kidney function tests',
            'Mammogram',
            'Pap smear HPV',
            'PSA',
            'XR / scans',
            'Other',
        ],
    },
    {
        value: 'auto-immune-disorders',
        label: 'Auto-immune disorders',
        categories: ['Rheumatoid arthritis', 'Sarcoidosis', 'SLE', 'Other'],
    },
    {
        value: 'blood-disorders',
        label: 'Blood disorders',
        categories: ['Clotting disorders', 'Red blood cells', 'White blood cells'],
    },
    {
        value: 'cancer',
        label: 'Cancer',
        categories: [
            'Breast',
            'Cervix',
            'Colon',
            'Melanoma',
            'Prostate',
            'Skin cancer BCC/SCC',
            'Other',
        ],
    },
    {
        value: 'cardiovascular-disorders',
        label: 'Cardiovascular disorders',
        categories: [
            'Arrhythmias',
            'Cardiomyopathy',
            'Coronary artery disease/MI',
            'High cholesterol',
            'Hypertension',
            'Pericardial disease',
            'Peripheral arterial disease',
            'Peripheral venous disease/ DVT / PE',
            'Valvular heart disease',
            'Other',
        ],
    },
    {
        value: 'central-nervous-system-disorders',
        label: 'Central nervous system disorders',
        categories: [
            'Benign brain tumour',
            'Cranial nerve',
            'Epilepsy',
            'Fainting / Syncope',
            'Gait disorders / Motor coordination',
            'Headache / Migraine',
            'History of coma / ICU or head injury',
            'Narcolepsy',
            'Organic / Congenital brain disorders',
            'Paralysis',
            'Tremor',
            'Trigeminal neuralgia',
            'Other',
        ],
    },
    {
        value: 'cerebrovascular-disorders',
        label: 'Cerebrovascular disorders',
        categories: ['Aneurism AV malformation', 'Stroke / TIA'],
    },
    {
        value: 'ear,-nose-and-throat-disorders',
        label: 'Ear, nose and throat disorders',
        categories: [
            'Hearing loss',
            'Vestibular disorders / Tinnitus / Meniere’s disease',
            'Voice / Speech impairment',
        ],
    },
    {
        value: 'endocrine-disorders',
        label: 'Endocrine disorders',
        categories: [
            'Diabetes / Abnormal blood glucose / Insulin resistance',
            'Metabolic syndrome',
            'PCOS',
            'Thyroid',
            'Other',
        ],
    },
    {
        value: 'family-history',
        label: 'Family history',
        categories: [
            'Breast / ovarian cancer',
            'Colon cancer',
            'CVD',
            'Diabetes',
            'Mental disorder',
            'Polycystic kidneys',
            'Prostate cancer',
            'Retinitis pigmentosa',
        ],
    },
    {
        value: 'gastrointestinal-disorders',
        label: 'Gastrointestinal disorders',
        categories: [
            'GORD / Barrett’s disease / Hiatus hernia',
            'Gall bladder',
            'Liver disease',
            'Ulcerative Colitis / Crohns disease',
            'Other',
        ],
    },
    {
        value: 'genitourinary-disorders',
        label: 'Genitourinary disorders',
        categories: ['Bladder', 'Ovary', 'Prostate', 'Urethra', 'Uterus', 'Other'],
    },
    { value: 'hiv/aids', label: 'HIV/AIDS', categories: ['HIV/AIDS'] },
    {
        value: 'mental-disorders',
        label: 'Mental disorders',
        categories: [
            'Bipolar disorder',
            'Depression/Anxiety/Stress',
            'Fibromyalgia / CFS',
            'PTSD',
            'Schizophrenia / Other psychotic disorders',
            'Other',
        ],
    },
    {
        value: 'musculo-skeletal-disorders',
        label: 'Musculo-skeletal disorders',
        categories: [
            'Arthritis',
            'Cervical spine',
            'Fractures',
            'Hernia other',
            'Joints - foot',
            'Joints - ankle',
            'Joints - elbow',
            'Joints - hand',
            'Joints - hip',
            'Joints - knee',
            'Joints - shoulder',
            'Joints - wrist',
            'Lumbar spine',
            'Thoracic spine',
            'Whole spine',
            'Other',
        ],
    },
    {
        value: 'renal-disorders',
        label: 'Renal disorders',
        categories: ['Kidney disorders', 'Kidney stones', 'Ureters', 'Other'],
    },
    {
        value: 'respiratory-disorders',
        label: 'Respiratory disorders',
        categories: ['Asthma', 'Pneumonia', 'Sleep apnoea', 'TB', 'Other'],
    },
    {
        value: 'skin-disorders',
        label: 'Skin disorders',
        categories: ['Dermatitis / eczema', 'Psoriasis', 'Other'],
    },
    {
        value: 'the-visual-system',
        label: 'The visual system',
        categories: [
            'Cataracts',
            'Glaucoma',
            'Loss of vision',
            'Macular degeneration',
            'Poor vision',
            'Retinal detachment',
            'Retinitis pigmentosa',
            'Other',
        ],
    },
];

type Props = StyledComponent & {
    nonDisclosureDetails?: NonDisclosureDetails[];
    onSave: (entries: NonDisclosureDetails[]) => void;
    onCancel: () => void;
};

function RetrospectiveUnderwritingViewComponent({ classes, nonDisclosureDetails = [] }: Props) {
    const form = useForm();
    const [localEntries, setLocalEntries] = useState<NonDisclosureDetails[]>([]);

    const createEmptyEntry = (): NonDisclosureDetails => ({
        additionalNonDisclosureReason: '',
        additionalNonDisclosureCategory: '',
        additionalNonDisclosureComment: '',
    });

    useEffect(() => {
        const initialEntries =
            nonDisclosureDetails.length > 0 ? [...nonDisclosureDetails] : [createEmptyEntry()];
        setLocalEntries(initialEntries);
    }, [nonDisclosureDetails]);

    useEffect(() => {
        form.change('nonDisclosureDetails', localEntries);
    }, [localEntries, form]);

    const handleAddEntry = () => {
        if (localEntries.length < 10) {
            setLocalEntries(prev => [...prev, createEmptyEntry()]);
        }
    };

    const handleRemoveEntry = (index: number) => {
        if (localEntries.length > 1) {
            setLocalEntries(prev => prev.filter((_, i) => i !== index));
        }
    };

    const handleFieldChange = (index: number, field: keyof NonDisclosureDetails, value: string) => {
        setLocalEntries(prev =>
            prev.map((entry, i) => (i === index ? { ...entry, [field]: value } : entry))
        );
    };

    return (
        <Paper className={classes.paper}>
            <Grid item xs={12}>
                <FormHeading text="Non-Disclosure Details" />
                <Grid container spacing={2}>
                    {localEntries.map((entry, index) => (
                        <Grid item xs={12} key={index}>
                            <Grid container alignItems="center" spacing={2}>
                                <Grid item xs={4}>
                                    <FullWidthSelectField
                                        name={`nonDisclosureDetails[${index}].additionalNonDisclosureReason`}
                                        label="Non-Disclosure Reason"
                                        value={entry.additionalNonDisclosureReason}
                                        onChange={(e: React.ChangeEvent<{ value: string }>) =>
                                            handleFieldChange(
                                                index,
                                                'additionalNonDisclosureReason',
                                                e.target.value
                                            )
                                        }
                                        options={REASON_OPTIONS}
                                        validate={requiredValidator}
                                    />
                                </Grid>

                                <Grid item xs={4}>
                                    <FullWidthSelectField
                                        name={`nonDisclosureDetails[${index}].additionalNonDisclosureCategory`}
                                        label="Non-Disclosure Category"
                                        value={entry.additionalNonDisclosureCategory}
                                        onChange={(e: React.ChangeEvent<{ value: string }>) =>
                                            handleFieldChange(
                                                index,
                                                'additionalNonDisclosureCategory',
                                                e.target.value
                                            )
                                        }
                                        options={
                                            REASON_OPTIONS.find(
                                                r => r.value === entry.additionalNonDisclosureReason
                                            )?.categories.map(cat => ({
                                                value: cat,
                                                label: cat,
                                            })) || []
                                        }
                                        disabled={!entry.additionalNonDisclosureReason}
                                        validate={requiredValidator}
                                    />
                                </Grid>

                                {entry.additionalNonDisclosureCategory === 'Other' && (
                                    <>
                                        <Grid item xs={3}>
                                            <FullWidthTextField
                                                name={`nonDisclosureDetails[${index}].additionalNonDisclosureComment`}
                                                label="Other Comment"
                                                value={entry.additionalNonDisclosureComment || ''}
                                                onChange={(
                                                    e: React.ChangeEvent<HTMLInputElement>
                                                ) =>
                                                    handleFieldChange(
                                                        index,
                                                        'additionalNonDisclosureComment',
                                                        e?.target?.value
                                                    )
                                                }
                                                required
                                                validate={requiredValidator}
                                            />
                                        </Grid>
                                    </>
                                )}

                                <Grid item xs={1}>
                                    {localEntries.length > 1 && (
                                        <IconButton
                                            onClick={() => handleRemoveEntry(index)}
                                            disabled={localEntries.length <= 1}
                                        >
                                            <DeleteIcon color="error" />
                                        </IconButton>
                                    )}
                                </Grid>
                            </Grid>
                        </Grid>
                    ))}

                    <Grid item xs={12}>
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={handleAddEntry}
                            disabled={isCapturedOrLegthLessthan11()}
                        >
                            Add Non-Disclosure ({localEntries.length}/10)
                        </Button>
                    </Grid>
                </Grid>
            </Grid>
            <FormHeading text="Retrospective Underwriting Decision" />
            <Grid container spacing={5} justify="flex-start">
                <Grid item xs={12}>
                    <FullWidthRichTextField
                        id="fullDisclosureAtApplicationStage"
                        name="fullDisclosureAtApplicationStage"
                        label="Underwriting Decision (Based on full disclosure at application stage)"
                        helperText="Based on full disclosure at application stage"
                        showOcr={true}
                        customHeader="summary"
                    />
                    <FullWidthRichTextField
                        id="chiefMedicalOfficerComments"
                        name="chiefMedicalOfficerComments"
                        showOcr={true}
                        customHeader="summary"
                    />
                </Grid>
                <Grid item xs={12}>
                    <Grid item xs={12}>
                        <FullWidthRichTextField
                            name="additionalInformation"
                            label="Additional Information (Information relating to the Underwriting decision)"
                            showOcr={true}
                            helperText="Information relating to the Underwriting decision"
                            customHeader="summary"
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <FullWidthTextField
                            id="retrospectiveUnderwriter"
                            name="retrospectiveUnderwriter"
                            validate={conditionalRequired(
                                (values: RetrospectiveUnderwritingDecisionDetails) =>
                                    !!values.fullDisclosureAtApplicationStage ||
                                    !!values.additionalInformation ||
                                    !!values.chiefMedicalOfficerComments
                            )}
                            multiline
                        />
                    </Grid>
                </Grid>
            </Grid>
        </Paper>
    );

    function isCapturedOrLegthLessthan11() {
        let isBtnDisabled = false;
        if (
            localEntries.every(
                entry =>
                    entry.additionalNonDisclosureReason &&
                    entry.additionalNonDisclosureCategory &&
                    (entry.additionalNonDisclosureCategory !== 'Other' ||
                        entry.additionalNonDisclosureComment)
            ) &&
            localEntries.length < 10
        ) {
            isBtnDisabled = false;
        } else {
            isBtnDisabled = true;
        }

        return isBtnDisabled;
    }
}

const mapStateToProps = (state: any) => ({
    nonDisclosureDetails:
        getRetrospectiveUnderwritingDecision(state).decisionDetails?.nonDisclosureDetails || [],
});

const mapDispatchToProps = {
    onSave: (entries: NonDisclosureDetails[]) => ({
        type: 'SAVE_NON_DISCLOSURES',
        payload: entries,
    }),
    onCancel: () => ({ type: 'CANCEL_RETROSPECTIVE_UNDERWRITING' }),
};

export const RetrospectiveUnderwritingView = withStyles(styles)(
    connect(mapStateToProps, mapDispatchToProps)(RetrospectiveUnderwritingViewComponent)
);
